import React, { useEffect, useState, Suspense, useMemo, useRef } from "react";
import "react-json-view-lite/dist/index.css";
import "./json-overrides.css";
// ✅ Lazy-load the named export
const LazyJsonViewer = React.lazy(async () => {
	const mod = await import("react-json-view-lite");
	return { default: mod.JsonView };
});

// ✅ darkStyles manually typed
const { darkStyles } = require("react-json-view-lite") as { darkStyles: any };

type JSONInput = object | string | null;

type Props = {
	data?: JSONInput;
	file?: File | null;
	url?: string | null;
	placeholder?: React.ReactNode;
	style?: React.CSSProperties;
	expandDepth?: number;
	onWheel?: (e: React.WheelEvent<HTMLDivElement>) => void;
};

function parseJSONSafe(txt: string): object | null {
	try {
		return JSON.parse(txt);
	} catch {
		return null;
	}
}

export default function MetadataViewer({ data = null, file = null, url = null, placeholder = "Loading JSON viewer…", style, expandDepth = 1, onWheel }: Props) {
	const [jsonObj, setJsonObj] = useState<object | null>(null);
	const [loadingData, setLoadingData] = useState(true);

	useEffect(() => {
		let cancelled = false;

		async function load() {
			setLoadingData(true);
			try {
				if (file) {
					const text = await file.text();
					if (!cancelled) setJsonObj(parseJSONSafe(text));
				} else if (url) {
					const resp = await fetch(url, { cache: "no-cache" });
					const txt = await resp.text();
					if (!cancelled) setJsonObj(parseJSONSafe(txt));
				} else if (data) {
					setJsonObj(typeof data === "string" ? parseJSONSafe(data) : (data as object));
				} else {
					setJsonObj(null);
				}
			} catch {
				if (!cancelled) setJsonObj(null);
			} finally {
				if (!cancelled) setLoadingData(false);
			}
		}

		load();
		return () => {
			cancelled = true;
		};
	}, [file, url, data]);

	const noBgDefault = {
		...darkStyles,
		container: `${darkStyles.container} my-json-container`,
		stringValue: `${darkStyles.stringValue} my-json-string`,
		numberValue: `${darkStyles.numberValue} my-json-number`,
		booleanValue: `${darkStyles.booleanValue} my-json-boolean`,
		nullValue: `${darkStyles.nullValue} my-json-null`,
		label: `${darkStyles.label} my-json-key`,
		clickableLabel: `${darkStyles.clickableLabel} my-json-key`, // for expandable keys
	};
	const noBgDefaultRef = useRef(noBgDefault);

	const memoizedViewer = useMemo(() => {
		if (!jsonObj) return null;
		return (
			<LazyJsonViewer
				data={jsonObj}
				clickToExpandNode
				style={noBgDefaultRef.current} // ✅ now safe typing
				shouldExpandNode={(level: number, value: any, field?: string) => level < expandDepth}
			/>
		);
	}, [jsonObj, expandDepth]);

	return (
		<div
			style={{
				width: "100%",
				height: "100%",

				scrollBehavior: "smooth",

				...style,
			}}
			//onWheel={onWheel}
		>
			<Suspense fallback={<div>{placeholder}</div>}>{loadingData ? <div>Loading JSON…</div> : jsonObj === null ? <div>No JSON available</div> : memoizedViewer}</Suspense>
		</div>
	);
}
